import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from './api';
import { rememberVerifiedKioskSession, forgetVerifiedKioskSession } from './offlineKioskAccess';
import { PRODUCT_IDENTITIES, brandForLocation, loginDestination, rememberProduct, productFromSearch } from './productIdentity';
import './ProductIdentity.css';

function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const fromState = location.state?.from || null;
  const product = brandForLocation(location, window.location.hostname);
  const nextPath = loginDestination(location, product);
  const isBefidLogin = String(fromState?.pathname || '').trim() === '/fid';
  const identity = PRODUCT_IDENTITIES[product] || {
    name: product.toUpperCase(),
    loginSubtitle: 'Vos commandes, simplement maîtrisées.',
  };
  const brandName = isBefidLogin ? 'BEFID' : identity.name;
  const brandLogoUrl = `/static/branding-v5/${isBefidLogin ? 'befid' : product}/compact.svg`;
  const explicitEntry = Boolean(productFromSearch(location.search) || productFromSearch(fromState?.search));
  useEffect(() => { rememberProduct(product, explicitEntry); }, [product, explicitEntry]);
  const copy = isBefidLogin ? {
    kicker: 'Connexion BEFID',
    title: 'App avis & photo souvenir',
    description: 'Connecte la tablette au compte restaurant pour afficher le QR d’avis, prendre la photo client et imprimer le souvenir avec le bon logo.',
    cardTitle: 'Compte BEFID',
    cardText: 'Utilise les identifiants de la borne rattachée à ce restaurant.',
    footnote: 'Les paramètres BEFID restent pilotés depuis Admin > Clients > Ecran fid.',
    points: [
      ['QR avis', 'URL Google ou landing configurée dans BEKIOSK.'],
      ['Photo souvenir', 'Capture tablette puis impression immédiate.'],
      ['Imprimante', 'Logo, QR et imprimante réglés dans l’admin.'],
    ],
  } : {
    kicker: 'Back office borne',
    title: 'Pilotage de la borne',
    description: 'Connecte-toi au compte de la borne pour gérer le catalogue, les réglages d’affichage, l’impression et le suivi des commandes.',
    cardTitle: 'Compte borne',
    cardText: 'Utilise les identifiants associés à cette borne.',
    footnote: 'Accès réservé aux comptes borne reconnus ou authentifiés.',
    points: [
      ['Catalogue', 'Produits, menus, suppléments et visuels.'],
      ['Expérience borne', 'Écran d’accueil, couleurs, messages et fidélisation.'],
      ['Opérations', 'Imprimantes, flux de commande et paramètres d’encaissement.'],
    ],
  };

  useEffect(() => {
    let active = true;
    const autoLogin = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/auto-login`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          if (response.status !== 401 && response.status !== 403) return;
          forgetVerifiedKioskSession();
          localStorage.removeItem('authToken');
          localStorage.removeItem('kioskAccount');
          return;
        }
        const data = await response.json();
        if (!active || !data?.success) {
          return;
        }
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('kioskAccount', JSON.stringify(data.account || {}));
        rememberVerifiedKioskSession(data.token, data.account);
        navigate(nextPath, { replace: true });
      } catch (err) {
        // A network failure is not a revoked session. Keep the bounded cache lease.
      }
    };
    autoLogin();
    return () => {
      active = false;
    };
  }, [navigate, nextPath]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const contentType = String(response.headers.get('content-type') || '').toLowerCase();
      const data = contentType.includes('application/json') ? await response.json() : null;

      if (response.ok) {
        localStorage.setItem('authToken', data?.token || '');
        localStorage.setItem('kioskAccount', JSON.stringify(data?.account || {}));
        rememberVerifiedKioskSession(data?.token, data?.account);
        navigate(nextPath, { replace: true });
      } else {
        setError(data?.message || 'Connexion refusée.');
      }
    } catch (err) {
      setError('Impossible de contacter le serveur. Vérifiez votre connexion et réessayez.');
    } finally {
      setLoading(false);
    }
  };

  if (!isBefidLogin) {
    return (
      <main className={`admin-login-page admin-login-page--bestock product-theme product-theme--${product}`}>
        <div className="admin-login-bestock-grid" aria-hidden="true" />
        <div className="admin-login-bestock-glow admin-login-bestock-glow-one" aria-hidden="true" />
        <div className="admin-login-bestock-glow admin-login-bestock-glow-two" aria-hidden="true" />

        <section className="admin-login-bestock-panel" aria-labelledby="beborne-login-title">
          <div className="admin-login-bestock-brand">
            <img src={brandLogoUrl} alt={brandName} className="admin-login-bestock-logo" />
            <div className="admin-login-bestock-brand-copy">
              <span className="admin-login-bestock-kicker">Espace professionnel</span>
              <h1 id="beborne-login-title">Bienvenue sur {brandName}</h1>
              <p>{identity.loginSubtitle}</p>
            </div>
          </div>

          <div className="admin-login-bestock-divider" aria-hidden="true" />

          <form onSubmit={handleLogin} className="admin-login-bestock-form" aria-label={`Connexion ${brandName}`}>
            {error && <div className="admin-login-bestock-error" role="alert">{error}</div>}

            <label className="admin-login-bestock-field">
              <span>Nom du compte</span>
              <span className="admin-login-bestock-input-wrap">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21a8 8 0 0 0-16 0M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" /></svg>
                <input
                  type="text"
                  placeholder="Nom de votre établissement"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                  required
                />
              </span>
            </label>

            <label className="admin-login-bestock-field">
              <span>Mot de passe</span>
              <span className="admin-login-bestock-input-wrap">
                <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
                <input
                  type="password"
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </span>
            </label>

            <button type="submit" className="admin-login-bestock-submit" disabled={loading} aria-busy={loading}>
              <span>{loading ? 'Connexion en cours...' : 'Se connecter'}</span>
            </button>
          </form>

          <p className="admin-login-bestock-security">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
            Connexion sécurisée
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className={`admin-login-page ${isBefidLogin ? 'is-befid' : ''}`}>
      <section className="admin-login-shell">
        <div className="admin-login-hero">
          <span className="kiosk-eyebrow">{copy.kicker}</span>
          <div className="admin-login-brand">
            <img src={brandLogoUrl} alt={brandName} />
          </div>
          <div className="admin-login-copy">
            <h1>{copy.title}</h1>
            <p>{copy.description}</p>
          </div>
          <div className="admin-login-points">
            {copy.points.map(([title, text]) => (
              <div className="admin-login-point" key={title}>
                <strong>{title}</strong>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-login-card">
          <div className="admin-login-card-head">
            <span className="admin-login-kicker">Connexion sécurisée</span>
            <h2>{copy.cardTitle}</h2>
            <p>{copy.cardText}</p>
          </div>

          <form onSubmit={handleLogin} className="admin-login-form">
            <label className="admin-login-field">
              <span>Nom du compte</span>
              <input
                type="text"
                placeholder="Ex. maythai"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </label>

            <label className="admin-login-field">
              <span>Mot de passe</span>
              <input
                type="password"
                placeholder="Votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            <button type="submit" className="admin-button admin-login-submit" disabled={loading}>
              {loading ? 'Connexion...' : 'Accéder au tableau de bord'}
            </button>
          </form>

          {error && <p className="kiosk-inline-error admin-login-error">{error}</p>}

          <div className="admin-login-footnote">
            <span>{copy.footnote}</span>
          </div>
        </div>
      </section>
    </main>
  );
}

export default AdminLogin;
